// A Live arrangement as Audiotool intents: CanonicalSet → PushPlan.
//
// The plan is declarative and keyed by LOCAL identities (lane keys, region
// keys) — never by Audiotool entity ids, which only the reconciler and the
// writer know. It is rebuilt from scratch on every push; push-reconcile.ts
// compares it with what was last written and emits the minimum set of ops.
//
// Entity facts this relies on are pinned by extension/tools/audiotool-spike.ts
// in docs/project/audiotool-entities.md; the ids below (A1, A3, …) refer to it.
//
// What maps:
//   • tempo → config.tempoBpm, clamped to 30–1000.
//   • time signature → config.signature*, ONLY when the caller supplies one.
//     Live's snapshot carries none, and a guess written into a multiplayer doc
//     would overwrite a signature a human set on purpose. Positions are
//     quarter-note based, so a wrong signature misdraws bar lines and never
//     moves a clip.
//   • lanes → audioTrack / noteTrack, ordered audio-first then MIDI (the
//     lanesOf() index space). orderAmongTracks is ONE space across audio and
//     note tracks and duplicates are rejected (A10); `ord` here is relative —
//     the writer offsets it past any human-made tracks.
//   • track mute / solo / fader / pan → the lane's mixer strip (isMuted,
//     isSoloed, postGain, panning — spike TR1). Live's fader tops out at +6 dB
//     and so does postGain ([0, 1.995]), so the level maps through dB exactly.
//     Renders are pre-FX, so this is the ONLY way Live's mix reaches Audiotool.
//   • audio clips → audioRegion on the lane's track (insertSample with
//     attachTo, A6). Overlaps stay on one lane — they are legal (A3).
//   • clip mute → region.isEnabled=false; clip name → region.displayName (A1).
//     Both are updatable in place (A1b), so they live in their own `attrs`
//     fingerprint and never force a re-insert.
//   • MIDI clips → noteRegion + noteCollection + notes (A7). Notes arrive
//     flattened past loops and clip-relative (shared/midi/notes.ts), so this is
//     a pure unit change: beats → ticks, 0–127 → 0–1.
//
// What doesn't, and says so (one aggregated warning per code per push):
//   • note mute (a muted note doesn't play, so it is dropped);
//     note probability / velocity deviation / release velocity (the note is
//     kept, the extra dropped — Audiotool's `note` has none of them).
//   • clip colour: Audiotool's colorIndex is a 0–41 palette whose meaning is
//     "up to the UI" (A1d), so there is no faithful mapping. Left at default,
//     silently — it is cosmetic and a warning for it would be noise.
//   • warp settings and markers: not needed for "clip-render" audio, which has
//     them baked in. See PushAudio.kind.
//
// Skipped, with a warning: a clip with no rendered audio (a receiver cannot
// materialise a region with no sample — hydrate.ts makes the same call), a
// zero-length clip, a MIDI clip whose notes were omitted on capture.
//
// Pure: no I/O, no SDK. Tested from workers/test/audiotool-push-plan.spec.ts.

import type { WireNote } from "../midi/notes.js";
import { lanesOf, type CanonicalClip, type CanonicalSet, type CanonicalTrack } from "../sync/canonical.js";
import { parseTrackName } from "../sync/track-id.js";
import { CENTER_PAN, clampPan, faderToGain } from "../mixer.js";
import { fingerprint } from "./digest.js";
import { beatsToTicks, ceilToBar, clampTempo, spanTicks, velocityToUnit } from "./ticks.js";

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

/** What a content tag's bytes actually ARE — the single most important input. */
export interface PushAudio {
  /** Content tag of the bytes (sha-256/8, realtime/storage.ts contentTag). */
  tag: string;
  displayName: string;
  /**
   * "clip-render": the bytes are exactly what the clip PLAYS over its
   *   arrangement span (renderClipAudio / sliceArrangementAudio). Its start
   *   marker, loop repeats and warping are ALREADY BAKED IN — re-applying them
   *   as offsetTicks/loop would double-apply them, which is the first bug this
   *   planner exists to make impossible.
   * "source-file": the bytes are the whole source the clip windows into, so
   *   the marker and loop map onto the region.
   */
  kind: "clip-render" | "source-file";
  /** source-file only: the source's musical length, in beats. */
  sourceBeats?: number;
}

/** Ties an audio object to a clip. `laneIndex` is in the lanesOf() space. */
export interface PushPlacement {
  laneIndex: number;
  clipIndex: number;
  tag: string;
}

/** A lane's Live mixer, as read from the Snapshot (CanonicalSet excludes it). */
export interface LaneMix {
  /** Live fader position, 0–1 (UNITY_VOLUME = 0 dB). */
  volume: number;
  /** −1…1. */
  panning: number;
}

export interface PushOptions {
  /** See the header: null leaves the Audiotool project's own signature alone. */
  signature: { numerator: number; denominator: number } | null;
  /** Per lane, in the lanesOf() space. A lane without one gets unity/centre. */
  mix?: readonly (LaneMix | undefined)[];
}

/** mixerChannel postGain's ceiling: +6 dB, the top of Live's fader too. */
export const MAX_STRIP_GAIN = 1.9952620267868042;

// ---------------------------------------------------------------------------
// Output
// ---------------------------------------------------------------------------

/** `sb:<token>` for a stamped lane; `a:<name>#<n>` / `m:<name>#<n>` otherwise. */
export type LaneKey = string;
/** `${laneKey}@${positionTicks}` — Live clips have no id; position is it. */
export type RegionKey = string;

/**
 * Three fingerprints per region, split by what it costs to reconcile a change:
 *   content  — needs a remove + re-create (the sample pointer is immutable,
 *              notes are separate entities);
 *   geometry — position/duration, a t.update in place (A1c);
 *   attrs    — isEnabled/displayName, a t.update in place (A1b).
 */
export interface RegionFingerprint {
  content: string;
  geometry: string;
  attrs: string;
}

export interface SetTempoIntent {
  kind: "setTempo";
  bpm: number;
}
export interface SetSignatureIntent {
  kind: "setSignature";
  numerator: number;
  denominator: number;
}
/** Grow config.durationTicks to at least this; never shrink a human's project. */
export interface EnsureDurationIntent {
  kind: "ensureDuration";
  minTicks: number;
}
export interface EnsureTrackIntent {
  kind: "ensureTrack";
  laneKey: LaneKey;
  trackKind: "audio" | "note";
  /** Relative order among the pushed lanes. */
  ord: number;
  displayName: string;
  /** Mixer strip state (spike TR1). */
  muted: boolean;
  soloed: boolean;
  /** Linear postGain, 0–MAX_STRIP_GAIN. */
  gain: number;
  panning: number;
}
export interface EnsureSampleIntent {
  kind: "ensureSample";
  tag: string;
  displayName: string;
}
export interface AudioRegionIntent {
  kind: "audioRegion";
  key: RegionKey;
  laneKey: LaneKey;
  tag: string;
  displayName: string;
  positionTicks: number;
  durationTicks: number;
  /** Where in the sample playback starts. 0 for clip-render audio. */
  offsetTicks: number;
  /** The sample's musical length — pins its stretch regardless of tempo. */
  musicDurationTicks: number;
  loop: false | { startTicks: number; durationTicks: number };
  isEnabled: boolean;
  fp: RegionFingerprint;
}
export interface PushNote {
  /** 0–127, unchanged: 60 = C4 on both sides. */
  pitch: number;
  /** Relative to the region's start. */
  positionTicks: number;
  /** Floored at 1 — a zero-length note is not a note. */
  durationTicks: number;
  /** 0–1. */
  velocity: number;
}
export interface NoteRegionIntent {
  kind: "noteRegion";
  key: RegionKey;
  laneKey: LaneKey;
  displayName: string;
  positionTicks: number;
  durationTicks: number;
  notes: PushNote[];
  isEnabled: boolean;
  fp: RegionFingerprint;
}

export type PushIntent =
  | SetTempoIntent
  | SetSignatureIntent
  | EnsureDurationIntent
  | EnsureTrackIntent
  | EnsureSampleIntent
  | AudioRegionIntent
  | NoteRegionIntent;

export type PushWarningCode =
  | "tempo-clamped"
  | "tempo-invalid"
  | "no-audio-for-clip"
  | "zero-length-clip"
  | "notes-omitted"
  | "muted-note-dropped"
  | "note-extras-dropped";

export interface PushWarning {
  code: PushWarningCode;
  /** User-facing. */
  detail: string;
  count: number;
}

export interface PushPlan {
  intents: PushIntent[];
  /** Distinct sample tags, in first-use order. */
  sampleTags: string[];
  warnings: PushWarning[];
}

// ---------------------------------------------------------------------------

const WARNING_TEXT: Record<PushWarningCode, string> = {
  "tempo-clamped": "The tempo is outside Audiotool's 30–1000 BPM range and was clamped.",
  "tempo-invalid": "The Set's tempo couldn't be read, so Audiotool's tempo was left alone.",
  "no-audio-for-clip": "Some clips had no rendered audio and were left out.",
  "zero-length-clip": "Some zero-length clips were left out.",
  "notes-omitted": "Some MIDI clips had too many notes to capture and were left out.",
  "muted-note-dropped": "Muted MIDI notes were left out (Audiotool notes can't be muted).",
  "note-extras-dropped":
    "Note probability, velocity deviation and release velocity don't exist in Audiotool and were dropped.",
};

class Warnings {
  private readonly counts = new Map<PushWarningCode, number>();
  add(code: PushWarningCode, n = 1): void {
    this.counts.set(code, (this.counts.get(code) ?? 0) + n);
  }
  list(): PushWarning[] {
    return [...this.counts].map(([code, count]) => ({ code, detail: WARNING_TEXT[code], count }));
  }
}

/**
 * Lane identities, in the lanesOf() index space.
 *
 * A stamped lane is keyed by its token — it survives renames and reorders. An
 * unstamped one (outside a jam, nothing has stamped it) falls back to kind +
 * display name + occurrence among same-named lanes of that kind: it survives a
 * reorder but not a rename, which is the best a name can do.
 */
export function laneKeysOf(set: CanonicalSet): LaneKey[] {
  const seen = new Map<string, number>();
  return lanesOf(set).map((t, i) => {
    const { display, token } = parseTrackName(t.name);
    if (token) return `sb:${token}`;
    const base = `${isMidiLane(set, i) ? "m" : "a"}:${display}`;
    const n = seen.get(base) ?? 0;
    seen.set(base, n + 1);
    return `${base}#${n}`;
  });
}

function isMidiLane(set: CanonicalSet, laneIndex: number): boolean {
  return laneIndex >= set.audioTracks.length;
}

export function planPush(
  set: CanonicalSet,
  audio: ReadonlyMap<string, PushAudio>,
  placements: readonly PushPlacement[],
  opts: PushOptions,
): PushPlan {
  const warnings = new Warnings();
  const head: PushIntent[] = [];
  const tracks: EnsureTrackIntent[] = [];
  const body: PushIntent[] = [];
  const sampleTags: string[] = [];
  const ensured = new Set<string>();
  let lastEnd = 0;

  const tempo = clampTempo(set.song.tempo);
  if (!Number.isFinite(set.song.tempo) || set.song.tempo <= 0) {
    warnings.add("tempo-invalid");
  } else {
    head.push({ kind: "setTempo", bpm: tempo.bpm });
    if (tempo.clamped) warnings.add("tempo-clamped");
  }
  if (opts.signature) {
    head.push({ kind: "setSignature", numerator: opts.signature.numerator, denominator: opts.signature.denominator });
  }

  const tagOf = new Map<string, string>();
  for (const p of placements) tagOf.set(`${p.laneIndex}:${p.clipIndex}`, p.tag);

  const lanes = lanesOf(set);
  const keys = laneKeysOf(set);

  lanes.forEach((lane: CanonicalTrack, laneIndex: number) => {
    const laneKey = keys[laneIndex]!;
    const midi = isMidiLane(set, laneIndex);
    const mix = opts.mix?.[laneIndex];
    tracks.push({
      kind: "ensureTrack",
      laneKey,
      trackKind: midi ? "note" : "audio",
      ord: laneIndex,
      displayName: parseTrackName(lane.name).display,
      muted: lane.mute,
      soloed: lane.solo,
      gain: mix ? Math.min(MAX_STRIP_GAIN, faderToGain(mix.volume)) : 1,
      panning: mix ? clampPan(mix.panning) : CENTER_PAN,
    });

    lane.arrangementClips.forEach((c: CanonicalClip, clipIndex: number) => {
      const span = spanTicks(c.startTime, c.startTime + c.duration);
      if (span.durationTicks <= 0) {
        warnings.add("zero-length-clip");
        return;
      }
      const intent = midi
        ? noteRegion(c, laneKey, span, warnings)
        : audioRegion(c, laneKey, span, audio.get(tagOf.get(`${laneIndex}:${clipIndex}`) ?? ""), warnings);
      if (!intent) return;

      if (intent.kind === "audioRegion" && !ensured.has(intent.tag)) {
        ensured.add(intent.tag);
        sampleTags.push(intent.tag);
        body.push({ kind: "ensureSample", tag: intent.tag, displayName: audio.get(intent.tag)?.displayName ?? intent.tag });
      }
      body.push(intent);
      lastEnd = Math.max(lastEnd, span.positionTicks + span.durationTicks);
    });
  });

  if (lastEnd > 0) {
    const sig = opts.signature ?? { numerator: 4, denominator: 4 };
    head.push({ kind: "ensureDuration", minTicks: ceilToBar(lastEnd, sig.numerator, sig.denominator) });
  }

  return { intents: [...head, ...tracks, ...body], sampleTags, warnings: warnings.list() };
}

function audioRegion(
  c: CanonicalClip,
  laneKey: LaneKey,
  span: { positionTicks: number; durationTicks: number },
  source: PushAudio | undefined,
  warnings: Warnings,
): AudioRegionIntent | null {
  if (!source) {
    warnings.add("no-audio-for-clip");
    return null;
  }
  let offsetTicks = 0;
  let loop: AudioRegionIntent["loop"] = false;
  let musicDurationTicks = span.durationTicks;
  if (source.kind === "source-file") {
    offsetTicks = beatsToTicks(c.startMarker);
    if (c.looping && c.loopEnd > c.loopStart) {
      const l = spanTicks(c.loopStart, c.loopEnd);
      loop = { startTicks: l.positionTicks, durationTicks: l.durationTicks };
    }
    musicDurationTicks = beatsToTicks(source.sourceBeats ?? c.endMarker);
  }
  const content = { tag: source.tag, offsetTicks, loop, musicDurationTicks };
  const attrs = { isEnabled: !c.muted, displayName: c.name };
  return {
    kind: "audioRegion",
    key: `${laneKey}@${span.positionTicks}`,
    laneKey,
    tag: source.tag,
    displayName: c.name,
    positionTicks: span.positionTicks,
    durationTicks: span.durationTicks,
    offsetTicks,
    musicDurationTicks,
    loop,
    isEnabled: !c.muted,
    fp: {
      content: fingerprint(content),
      geometry: fingerprint(span),
      attrs: fingerprint(attrs),
    },
  };
}

function noteRegion(
  c: CanonicalClip,
  laneKey: LaneKey,
  span: { positionTicks: number; durationTicks: number },
  warnings: Warnings,
): NoteRegionIntent | null {
  if (!c.notes) {
    warnings.add("notes-omitted");
    return null;
  }
  const notes: PushNote[] = [];
  for (const n of c.notes) {
    if (n.muted) {
      warnings.add("muted-note-dropped");
      continue;
    }
    if (hasExtras(n)) warnings.add("note-extras-dropped");
    const s = spanTicks(n.startTime, n.startTime + n.duration);
    notes.push({
      pitch: Math.min(127, Math.max(0, Math.round(n.pitch))),
      positionTicks: s.positionTicks,
      durationTicks: Math.max(1, s.durationTicks),
      velocity: velocityToUnit(n.velocity),
    });
  }
  const attrs = { isEnabled: !c.muted, displayName: c.name };
  return {
    kind: "noteRegion",
    key: `${laneKey}@${span.positionTicks}`,
    laneKey,
    displayName: c.name,
    positionTicks: span.positionTicks,
    durationTicks: span.durationTicks,
    notes,
    isEnabled: !c.muted,
    fp: {
      content: fingerprint(notes),
      geometry: fingerprint(span),
      attrs: fingerprint(attrs),
    },
  };
}

/** Live-only note fields that carry a non-default value. */
function hasExtras(n: WireNote): boolean {
  return (
    (n.probability !== undefined && n.probability !== 1) ||
    (n.velocityDeviation !== undefined && n.velocityDeviation !== 0) ||
    (n.releaseVelocity !== undefined && n.releaseVelocity !== 64)
  );
}
